"use client";

import { createContext, useContext } from "react";
import type { MultiFactorResolver } from "firebase/auth";
import type { User } from "./user";

export type { User } from "./user";

export interface AuthContextValue {
  user: User | null;
  isAuthenticating: boolean;
  /**
   * Set when establishing the server session failed after sign-in (AIR-819)
   * — the login page surfaces it so a failed handoff never ends in a silent
   * bounce back to the form. Cleared when a new attempt starts or succeeds.
   */
  authError: string | null;
  /**
   * A sign-in that Firebase stopped for a second factor (AIR-1500) on the
   * redirect path — Google/Microsoft in production return to the page via
   * getRedirectResult, where the provider is the only code that sees the
   * error. The login page picks the resolver up and asks for the code.
   */
  mfaChallenge: MultiFactorResolver | null;
  clearMfaChallenge: () => void;
}

export const AuthContext = createContext<AuthContextValue>({
  user: null,
  isAuthenticating: false,
  authError: null,
  mfaChallenge: null,
  clearMfaChallenge: () => undefined,
});

export const useAuth = () => useContext(AuthContext);
